import { FC, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useGuardianState } from '../../../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianEventsHook } from '../../../../hooks/useGuardianEventsCommunicationHook';
import PaginatedGrid from '../../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Event, School, Sports, TheaterComedy, Build, Search, Clear, ViewList, CalendarMonth } from '@mui/icons-material';
import CalendarComponent from '../../../../../../components/calendarComponent/calendarComponent';
import { EventInput } from '@fullcalendar/core';

const GuardianEventsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data, isLoading, error } = useGuardianEventsHook(
    selectedChildId || '',
    {
      page,
      limit: pageSize,
      ...(typeFilter !== 'all' && { type: typeFilter }),
      ...(statusFilter !== 'all' && { status: statusFilter }),
    }
  );

  const events = useMemo(() => data?.data || [], [data]);

  const filteredEvents = useMemo(() => {
    if (!searchQuery) return events;
    const query = searchQuery.toLowerCase();
    return events.filter(
      (event) =>
        event.title.toLowerCase().includes(query) ||
        event.description.toLowerCase().includes(query) ||
        event.location.toLowerCase().includes(query)
    );
  }, [events, searchQuery]);

  const eventTypes = useMemo(() => {
    const uniqueTypes = Array.from(new Set(events.map((e) => e.type)));
    return uniqueTypes.sort();
  }, [events]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'school':
        return <School />;
      case 'sports':
        return <Sports />;
      case 'cultural':
        return <TheaterComedy />;
      case 'class':
        return <Event />;
      default:
        return <Build />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'upcoming':
        return 'default';
      case 'ongoing':
        return 'warning';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getEventTypeColor = (type: string): string => {
    switch (type) {
      case 'school':
        return '#1976d2';
      case 'class':
        return '#388e3c';
      case 'sports':
        return '#f57c00';
      case 'cultural':
        return '#7b1fa2';
      default:
        return '#616161';
    }
  };

  const calendarEvents = useMemo<EventInput[]>(() => {
    return filteredEvents.map((event) => ({
      id: event.id,
      title: event.title,
      start: event.date + (event.time ? `T${event.time}` : ''),
      backgroundColor: getEventTypeColor(event.type),
      borderColor: getEventTypeColor(event.type),
      extendedProps: {
        ...event,
      },
    }));
  }, [filteredEvents]);

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'type',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return getTypeIcon(params.value as string);
      },
    },
    {
      field: 'title',
      headerName: translateText('guardian.eventsCommunication.events.columns.title'),
      width: 200,
      flex: 1,
    },
    {
      field: 'date',
      headerName: translateText('guardian.eventsCommunication.events.columns.date'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      field: 'time',
      headerName: translateText('guardian.eventsCommunication.events.columns.time'),
      width: 120,
    },
    {
      field: 'location',
      headerName: translateText('guardian.eventsCommunication.events.columns.location'),
      width: 150,
    },
    {
      field: 'type',
      headerName: translateText('guardian.eventsCommunication.events.columns.type'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            label={translateText(`guardian.eventsCommunication.events.types.${params.value}`)}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'status',
      headerName: translateText('guardian.eventsCommunication.events.columns.status'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as string;
        return (
          <Chip
            label={translateText(`guardian.eventsCommunication.events.status.${status}`)}
            size="small"
            color={getStatusColor(status) as any}
            variant="outlined"
          />
        );
      },
    },
  ], [translateText]);

  const rows = useMemo(() => {
    return filteredEvents.map((event) => ({
      id: event.id,
      title: event.title,
      date: event.date,
      time: event.time,
      location: event.location,
      type: event.type,
      status: event.status,
      description: event.description,
    }));
  }, [filteredEvents]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-events-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-events-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-events-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-events-container">
      <Grid item xs={12}>
        <Typography variant="h5">
          {translateText('guardian.eventsCommunication.events.title')}
        </Typography>
      </Grid>

      {/* Search and Filters */}
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          data-testid="guardian-events-filters"
        >
          <TextField
            fullWidth
            placeholder={translateText('guardian.eventsCommunication.events.search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    data-testid="guardian-events-search-clear"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
            data-testid="guardian-events-search"
          />
          {eventTypes.length > 0 && (
            <TextField
              select
              label={translateText('guardian.eventsCommunication.events.filters.type')}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={{ minWidth: 150 }}
              size="small"
              data-testid="guardian-events-type-filter"
            >
              <MenuItem value="all">{translateText('common.all')}</MenuItem>
              {eventTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {translateText(`guardian.eventsCommunication.events.types.${type}`)}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            label={translateText('guardian.eventsCommunication.events.filters.status')}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
            size="small"
            data-testid="guardian-events-status-filter"
          >
            <MenuItem value="all">{translateText('common.all')}</MenuItem>
            <MenuItem value="upcoming">{translateText('guardian.eventsCommunication.events.status.upcoming')}</MenuItem>
            <MenuItem value="ongoing">{translateText('guardian.eventsCommunication.events.status.ongoing')}</MenuItem>
            <MenuItem value="completed">{translateText('guardian.eventsCommunication.events.status.completed')}</MenuItem>
          </TextField>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            aria-label="view mode"
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                minHeight: '40px',
              },
            }}
            data-testid="guardian-events-view-mode-toggle"
          >
            <ToggleButton value="list" aria-label="list view" data-testid="toggle-list-view">
              <ViewList sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {translateText('guardian.eventsCommunication.events.viewMode.list')}
              </Box>
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="calendar view" data-testid="toggle-calendar-view">
              <CalendarMonth sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {translateText('guardian.eventsCommunication.events.viewMode.calendar')}
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Grid>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Grid item xs={12}>
          {calendarEvents.length === 0 ? (
            <Alert severity="info">
              {searchQuery
                ? translateText('guardian.eventsCommunication.events.noSearchResults')
                : translateText('guardian.eventsCommunication.events.noEvents')}
            </Alert>
          ) : (
            <Card>
              <CardContent>
                <CalendarComponent
                  events={calendarEvents}
                  data-testid="guardian-events-calendar"
                />
              </CardContent>
            </Card>
          )}
        </Grid>
      )}

      {/* Events Grid */}
      {viewMode === 'list' && (
        <Grid item xs={12}>
          {filteredEvents.length === 0 && !isLoading ? (
            <Alert severity="info">
              {searchQuery
                ? translateText('guardian.eventsCommunication.events.noSearchResults')
                : translateText('guardian.eventsCommunication.events.noEvents')}
            </Alert>
          ) : (
            <PaginatedGrid
              rows={rows}
              columns={columns}
              loading={isLoading}
              dataTestId="guardian-events-grid"
              serverSide={false}
              totalCount={filteredEvents.length}
              currentPage={page}
              currentPageSize={pageSize}
              onPageChange={handlePageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default GuardianEventsView;
